import { captureOnce, capturePaletteOpened, onSurfaceOpened } from "@reflecto/client-core";
import { type AIAvailability, ChromeBuiltinAIProvider } from "@reflecto/client-core/ai";
import {
  ClipDock,
  type ClipDockLabels,
  CommandPalette,
  type CommandPaletteLabels,
  type ConversationNotificationItemLabels,
  type DockClip,
  FooterHint,
  type FooterHintLabels,
  HeaderBar,
  type HeaderBarLabels,
  IconButton,
  type NotificationListItemLabels,
  type PaletteCommand,
  PublicModeToggle,
  SidePanelNotificationList,
  type SidePanelNotificationListLabels,
  useAIStatus,
} from "@reflecto/client-ui";
import { Stars01 } from "@untitledui/icons";
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { createWebClipboardCapabilities } from "../adapters/clipboard.web";
import { webStorage } from "../adapters/storage.web";
import { Glance } from "../components/Glance";
import { InstallPrompt } from "../components/InstallPrompt";
import { PairConfirmCardSlot } from "../components/PairConfirmCardSlot";
import { UpdatePrompt } from "../components/UpdatePrompt";
import { useExpiredNotificationPrune } from "../hooks/useExpiredNotificationPrune";
import { useStorage } from "../hooks/useStorage";
import { messages } from "../i18n";
import { installCommandPaletteListener, installLeaderChordListener } from "../lib/keyboard";
import { logWarn } from "../lib/log";
import { navigate, useRoute } from "../lib/router";
import {
  getAnalytics,
  trackGlanceViewed,
  trackNotificationsSeen,
  withCommandAnalytics,
} from "../services/analytics";
import { getClips, type StoredClip, subscribeClips } from "../services/clipsStore";
import { conversations as conversationsStore } from "../services/conversationsStore";
import { markAllRead, markNotificationsRead } from "../services/markRead";
import {
  dismissAllNotifications,
  dismissConversation,
  dismissNotification,
  dispatchAction,
  sendReply,
} from "../services/messages";
import styles from "./MainPage.module.css";
import { buildPaletteCommands } from "./paletteCommands";

/**
 * Web's primary notification surface.
 *
 * Hosts the headless `SidePanelNotificationList` from
 * `@reflecto/client-ui` — same grouping / search / timeline /
 * IntersectionObserver-auto-mark-read state machine the extension
 * uses, with platform-specific bindings injected here:
 *
 *   - data: `useStorage` reads of notifications/conversations/
 *     appIconCache/groupByApp/unreadCount; `Object.values()` flattens
 *     the conversations map (web stores it as a Record keyed by
 *     thread key, the headless component takes a flat array).
 *   - mark-read: inline storage mutators in `services/markRead`
 *     (no background worker — single React tree).
 *   - dismiss/reply/action: the same `services/messages` helpers the
 *     prior flat-list MainPage already used.
 *   - mark-conversation-read: shared `conversationsStore` factory
 *     (so the in-memory msgId index stays consistent across the SSE
 *     write path and this UI write path).
 *   - header slot: the shared `HeaderBar` with the live pairing-status
 *     dot (`paired` / `sseConnected` are passed through below). Web has
 *     no theme toggle yet.
 *
 * The SSE pipeline is owned by `App.tsx` (one level up) so navigating
 * to SettingsPage doesn't tear down the connection. Label bags below
 * are sourced from the `messages` catalog in `apps/web/src/i18n/`.
 */

// Label bags re-shape the catalog into the prop shapes each headless
// component expects. The headless contracts in `@reflecto/client-ui`
// dictate the keys (`replyAriaLabel`, `noMatchesDesc`, etc.); the
// catalog supplies the strings. Banner name matches the extension's
// `t("extActionTitle")` ("Reflecto") so screen-reader landmark
// navigation lands on the same identity across both surfaces.
const NOTIFICATION_LABELS: NotificationListItemLabels = {
  replyAriaLabel: messages.notifications.replyAriaLabel,
  replyPlaceholder: messages.notifications.replyPlaceholder,
  sendLabel: messages.notifications.replySend,
  sent: messages.notifications.sent,
  failedToSend: messages.notifications.failedToSend,
  unread: messages.notifications.unread,
  dismiss: messages.notifications.dismiss,
  notificationImageAlt: messages.notifications.notificationImageAlt,
};

const CONVERSATION_LABELS: ConversationNotificationItemLabels = {
  replyAriaLabel: messages.conversations.replyAriaLabel,
  replyPlaceholder: messages.conversations.replyPlaceholder,
  sendLabel: messages.conversations.replySend,
  sent: messages.conversations.sent,
  failedToSend: messages.conversations.failedToSend,
  // unreadAriaLabel is a function so its plural rule lives in the catalog —
  // a future locale can override it without touching this file.
  unreadAriaLabel: messages.conversations.unreadAriaLabel,
  dismiss: messages.conversations.dismiss,
  messagesListLabel: messages.conversations.messagesListLabel,
  deviceOwner: messages.conversations.deviceOwner,
};

const WEB_HEADER_LABELS: HeaderBarLabels = {
  banner: messages.header.banner,
  statusConnected: messages.header.statusConnected,
  statusReconnecting: messages.header.statusReconnecting,
  statusDisconnected: messages.header.statusDisconnected,
  openSettings: messages.header.openSettings,
};

const LIST_LABELS: SidePanelNotificationListLabels = {
  today: messages.list.today,
  yesterday: messages.list.yesterday,
  searchNotifications: messages.list.searchNotifications,
  clearSearch: messages.list.clearSearch,
  clearAll: messages.list.clearAll,
  clearMatches: messages.list.clearMatches,
  markAllRead: messages.list.markAllRead,
  groupCheckboxLabel: messages.list.groupCheckboxLabel,
  privacyCount: messages.list.privacyCount,
  noNotificationsTitle: messages.list.noNotificationsTitle,
  noNotificationsDesc: messages.list.noNotificationsDesc,
  noMatchesTitle: messages.list.noMatchesTitle,
  noMatchesDesc: messages.list.noMatchesDesc,
  notificationsList: messages.list.notificationsList,
  privacyAppList: messages.list.privacyAppList,
  appNotificationsGroup: messages.list.appNotificationsGroup,
  dismissAllFrom: messages.list.dismissAllFrom,
  clear: messages.list.clear,
  // keyboardHint intentionally omitted here — the keycap footer is rendered as
  // the shell's bottom-most row (below the ClipDock), not inside
  // SidePanelNotificationList. Keyboard handlers stay active via `enableKeyboard`.
};

// Keyboard keycap hint, rendered below the dock so the dock is the primary
// bottom chrome and the passive hint is the true footer.
const KEYBOARD_HINT: FooterHintLabels = {
  dismiss: messages.list.kbdDismiss,
  navigate: messages.list.kbdNavigate,
  orSeparator: messages.list.kbdOrSeparator,
  // activatePrompt omitted: web renders this footer without a `focused` prop
  // (always keycaps — a normal tab owns focus), so the unfocused prompt never shows.
};

// Stable, immutable server snapshot for `useSyncExternalStore` — clips are an
// in-memory session store with no SSR, so the server snapshot is always empty.
// A module-level constant keeps the reference identity stable per React's
// contract. (The infinite-re-render risk lives on the CLIENT snapshot — the 2nd
// `useSyncExternalStore` arg — where returning a fresh `[]` each call would loop;
// the server snapshot, this 3rd arg, is never called in the SPA, so `EMPTY_CLIPS`
// is purely about a stable immutable identity if SSR is ever added.)
const EMPTY_CLIPS: readonly StoredClip[] = [];

// Clipboard dock label bag — re-shapes `messages.clipboard.*` into the
// headless `ClipDock` contract. Several existing clipboard/card catalog keys
// are mapped onto the dock's differently-named props — e.g.
// sendNoDevices→sentNone, sendSuccess→sentToast, dockCancel→popover.cancel —
// alongside the directly-named reuses (from/copy/copied/reveal/hide/
// sensitiveChip/cloudChip/sendPlaceholder/sendCta/sendSensitiveToggle) plus the
// new `dock*` keys for the bar/popover/expanded chrome.
const CLIP_DOCK_LABELS: ClipDockLabels = {
  sectionLabel: messages.clipboard.title,
  bar: {
    empty: messages.clipboard.empty,
    copy: messages.clipboard.copy,
    copied: messages.clipboard.copied,
    send: messages.clipboard.sendCta,
    expand: messages.clipboard.dockExpand,
    from: messages.clipboard.from,
  },
  popover: {
    title: messages.clipboard.dockSendTitle,
    fromClipboard: messages.clipboard.dockFromClipboard,
    sensitiveToggle: messages.clipboard.sendSensitiveToggle,
    cancel: messages.clipboard.dockCancel,
    send: messages.clipboard.dockSend,
  },
  card: {
    from: messages.clipboard.from,
    copy: messages.clipboard.copy,
    copied: messages.clipboard.copied,
    reveal: messages.clipboard.reveal,
    hide: messages.clipboard.hide,
    sensitive: messages.clipboard.sensitiveChip,
    cloud: messages.clipboard.cloudChip,
    maskedHint: messages.clipboard.maskedHint,
  },
  recentHeading: messages.clipboard.dockRecentHeading,
  empty: messages.clipboard.empty,
  emptyClipboardTip: messages.clipboard.dockEmptyClipboard,
  // MVP: 1 partner only, so we explicitly drop ClipDock's `total` arg. Under
  // multi-device this would mis-render a partial send (e.g. "1 of 3"); revisit then.
  sentToast: (sent) => messages.clipboard.sendSuccess(sent),
  sentNone: messages.clipboard.sendNoDevices,
  composerPlaceholder: messages.clipboard.sendPlaceholder,
  composerSend: messages.clipboard.sendCta,
  collapse: messages.clipboard.dockCollapse,
};

// i18n label bag for the headless CommandPalette's chrome (placeholder / no-results /
// aria-label). Per-command labels are state-dependent (e.g. the privacy toggle's
// on/off text) and are picked inside `buildPaletteCommands` at assembly time instead.
const COMMAND_PALETTE_LABELS: CommandPaletteLabels = {
  placeholder: messages.palette.placeholder,
  noResults: messages.palette.noResults,
  ariaLabel: messages.palette.ariaLabel,
};

// Stable empty reference for when the palette is closed — see the
// `paletteCommands` memo below for why the build is skipped entirely then.
const NO_PALETTE_COMMANDS: PaletteCommand[] = [];

// Module-level for stable identity — useAIStatus puts these in effect deps. Shared between the
// header AI-recap button (capability gate) and the `Glance` card (both read one probe).
const aiStatusBindings = {
  loadInitial: async () => {
    const s = await webStorage.get("aiLastAvailability");
    return { lastAvailability: s.aiLastAvailability ?? undefined };
  },
  persistLastAvailability: (next: AIAvailability) => webStorage.set({ aiLastAvailability: next }),
};

async function handleMarkConversationRead(key: string): Promise<void> {
  try {
    await conversationsStore.markConversationRead(key);
  } catch (err) {
    // Headless calls this fire-and-forget (`void onMarkConversationRead(...)`)
    // per the prop's "caller-logged" contract — surface the failure here
    // since this is the host's storage-coupled DI seam.
    //
    // The conversation `key` (`packageName|id|tag`) is intentionally
    // omitted: it carries the Android app's package name, which the
    // architecture forbids the server from seeing
    // (apps/server/CLAUDE.md). Today this is a browser console.warn
    // — low risk — but when remote error reporting (Sentry/OTLP)
    // wires in front of `logWarn`, the metadata would leak to a
    // third-party sink. Cheaper to omit at source than redact later.
    logWarn("messages", "mark_conversation_read_failed", err);
  }
}

export function MainPage() {
  const { value: androidDeviceName } = useStorage("androidDeviceName");
  const { value: notifications } = useStorage("notifications");

  // Active User anchor (§4.2): debounced open event + drain of the SW's bookkeeping.
  useEffect(() => {
    void onSurfaceOpened(webStorage, getAnalytics(), "web_app_opened", "app_opens");
    void captureOnce(webStorage, getAnalytics(), "web_app_first_open");
  }, []);

  // Seen = rendered while visible (§3.3); session-scoped dedup. visibilitychange
  // re-runs the effect so a batch delivered while hidden counts on refocus.
  const seenIdsRef = useRef<Set<string>>(new Set());
  const [pageVisible, setPageVisible] = useState(document.visibilityState === "visible");
  useEffect(() => {
    const onVisibility = () => setPageVisible(document.visibilityState === "visible");
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);
  useEffect(() => {
    if (!notifications || !pageVisible) return;
    const fresh = notifications.filter((n) => !seenIdsRef.current.has(n.msgId));
    if (fresh.length === 0) return;
    for (const n of fresh) seenIdsRef.current.add(n.msgId);
    void trackNotificationsSeen(fresh.length);
  }, [notifications, pageVisible]);
  const { value: conversations } = useStorage("conversations");
  const { value: appIconCache } = useStorage("appIconCache");
  const { value: groupByApp } = useStorage("groupByApp");
  const isGrouped = groupByApp === true;

  const handleToggleGrouped = useCallback((checked: boolean) => {
    void webStorage.set({ groupByApp: checked });
  }, []);
  const { value: unreadCount } = useStorage("unreadCount");
  const { value: paired } = useStorage("paired");
  const { value: sseConnected } = useStorage("sseConnected");

  // TTL auto-dismiss for server-origin (`/v1/send`) notifications: prune
  // `expiresAt`-elapsed cards from storage on mount + on an interval. No-op for
  // device-mirrored notifications (they carry no `expiresAt`).
  useExpiredNotificationPrune(paired === true);

  // The pairing flow sets `pairingJustConfirmed: true` so the confirm
  // screen can animate. Flip it back to false shortly after landing
  // on MainPage \u2014 the extension does the same via setTimeout in
  // completePairing; the web app does it from the UI side to avoid
  // a stray timer surviving across tab closes.
  useEffect(() => {
    const t = setTimeout(() => {
      void webStorage.set({ pairingJustConfirmed: false });
    }, 3000);
    return () => clearTimeout(t);
  }, []);

  // Web stores conversations as a Record<key, ConversationThread> so
  // single-thread lookups + writes stay O(1). The headless component
  // takes a flat array and does its own grouping, so flatten here.
  // Memoised on the underlying record so unrelated parent re-renders
  // don't churn the headless component's effect deps.
  const conversationsList = useMemo(() => Object.values(conversations ?? {}), [conversations]);

  // Stable ref so HeaderBar doesn't see a fresh function identity on
  // every MainPage re-render — useStorage flips on every IDB write,
  // so MainPage re-renders frequently and an inline `() => navigate(
  // ...)` would defeat any downstream memoization keyed on the
  // callback prop. (HeaderBar today doesn't list `onOpenSettings` in
  // a useEffect dep, so this is preventive — but matches the
  // `onMainViewActive` stability rule the headless AppShell
  // documents.)
  const handleOpenSettings = useCallback(() => navigate({ name: "settings" }), []);

  // Public mode is in-memory only — V1 spec demands the flag reset to
  // OFF on every reload so the fail-safe is "showing notifications".
  // We deliberately don't persist this through `webStorage`. See the
  // matching note on the extension's SidePanelNotificationList wrapper
  // and the JSDoc on `ReflectoStorage.publicMode`.
  const [publicMode, setPublicMode] = useState(false);
  const togglePublicMode = useCallback(() => setPublicMode((v) => !v), []);

  // Inbound clipboard — the ephemeral session store (cap 20, never persisted).
  // Map each stored clip to the headless `DockClip` shape; the dock renders
  // the latest in its collapsed bar and the full list when expanded.
  const storedClips = useSyncExternalStore<readonly StoredClip[]>(
    subscribeClips,
    getClips,
    () => EMPTY_CLIPS
  );
  const dockClips: DockClip[] = useMemo(
    () =>
      storedClips.map((c) => ({
        id: c.id,
        content: c.payload.content,
        originLabel: c.payload.origin.label,
        timestamp: c.payload.timestamp,
        sensitive: c.payload.sensitive,
        provenance: c.payload.provenance,
      })),
    [storedClips]
  );
  // Outbound capabilities (clipboard read/write + broadcast fan-out). Stable
  // identity so the dock's callbacks don't churn on every MainPage re-render.
  const clipCaps = useMemo(() => createWebClipboardCapabilities(), []);

  // One AI probe drives both the header recap button's capability gate and the `Glance` card.
  // The card needs the full status (download / progress / errorMessage); the button needs only the
  // capability boolean — so own `useAIStatus` here and inject the status down to the card.
  const provider = useMemo(() => new ChromeBuiltinAIProvider(), []);
  const aiStatus = useAIStatus({ provider, ...aiStatusBindings });
  // AI-capable = the model is usable or installable: allowlist the usable availability states.
  // (A blocklist like `!== "unsupported"` lets "unavailable" — policy-blocked / model-removed —
  // through, rendering a button whose every trigger errors with no recovery path.) Also gate on the
  // live requirements report so a stale storage-restored "available" can't show the button before
  // the probe confirms (`null` = pre-probe).
  const aiCapable =
    (aiStatus.availability === "downloadable" ||
      aiStatus.availability === "downloading" ||
      aiStatus.availability === "available") &&
    (aiStatus.requirements?.ok ?? false);

  // On-demand AI recap: the header "Glance" button bumps this counter, which the headless
  // `Glance` watches as `trigger` to surface the card + run a last-24h recap. In-memory only —
  // a recap is a user-initiated action, nothing to persist.
  const [recapTrigger, setRecapTrigger] = useState(0);
  const handleGlance = useCallback(() => {
    setRecapTrigger((n) => n + 1);
    trackGlanceViewed();
  }, []);
  // Jump signal: the recap card calls `onJumpTo` with a target; bumping `signal` reveals + scrolls
  // + highlights that row in the notification list.
  const [jump, setJump] = useState<
    { target: { msgId: string } | { conversationKey: string }; signal: number } | undefined
  >(undefined);

  // SW deep-link: bump the shared `jump` signal; one-shot per key, waits out cold-start.
  const route = useRoute();
  const deepLinkedKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (route.name !== "conversation") {
      deepLinkedKeyRef.current = null;
      return;
    }
    const key = route.conversationKey;
    if (deepLinkedKeyRef.current === key) return;
    if (!conversationsList.some((c) => c.key === key)) return;
    deepLinkedKeyRef.current = key;
    setJump((j) => ({ target: { conversationKey: key }, signal: (j?.signal ?? 0) + 1 }));
  }, [route, conversationsList]);

  // Leader chord (PRD docs/prd-keyboard.md §5.8). macOS/Linux use
  // Ctrl+Shift+K, Windows uses Alt+Shift+K, macOS additionally accepts
  // Cmd+Shift+K (the manifest's `mac` slot in the extension uses
  // Command — the web honours both so muscle memory carries across
  // surfaces). Bumping focusSignal cascades into the headless list
  // which re-selects the first unread row.
  const [focusSignal, setFocusSignal] = useState(0);
  useEffect(() => {
    // Prefer User-Agent Client Hints (Chromium); `navigator.platform` is the deprecated fallback.
    const uaData = (navigator as { userAgentData?: { platform?: string } }).userAgentData?.platform;
    const legacy = typeof navigator.platform === "string" ? navigator.platform : "";
    return installLeaderChordListener({
      // `||` rather than `??` so an empty `uaData` falls back too.
      // `navigator.userAgentData.platform` returns a non-empty
      // string on every supporting Chromium today, but defensively
      // an empty string would otherwise pass through `??` and
      // mis-route the chord to the Linux branch on any OS.
      platform: uaData || legacy,
      // PRD §5.2: the leader is a modifier chord; the input-trap
      // guard (`shouldFireShortcut`) is for single-letter shortcuts
      // and would silently no-op summon while focus is in the
      // search box. Only block IME composition here — that's the
      // one state where a chord could collide with text input.
      shouldFire: (e) => !e.isComposing && e.code !== "Process",
      onSummon: () => setFocusSignal((n) => n + 1),
    });
  }, []);

  // ⌘K / Ctrl+K command palette (docs/superpowers/specs/2026-07-12-extension-command-palette-design.md
  // §11). Plain document-scoped keydown, registered beside (not instead of) the leader-chord
  // listener above — the two matchers are mutually exclusive on the shiftKey axis
  // (`isCommandPaletteChordEvent` rejects shiftKey, `isLeaderChordEvent` requires it), so
  // Shift+⌘K keeps summoning the column while plain ⌘K opens the palette. A second ⌘K while
  // already open toggles it closed — an internal UX choice, not spec'd, mirroring the extension.
  const [paletteOpen, setPaletteOpen] = useState(false);
  useEffect(() => {
    return installCommandPaletteListener({
      // Same IME-composition guard as the leader chord above — the one state where a modifier
      // chord could collide with text input.
      shouldFire: (e) => !e.isComposing && e.code !== "Process",
      onToggle: () => setPaletteOpen((open) => !open),
    });
  }, []);

  // Palette opened = recurring intent (event-spec §4.2), debounced ≥30 min —
  // independently of the web_app_opened anchor (see capturePaletteOpened's
  // doc comment: the palette only opens from inside an already-open page,
  // so sharing that anchor would suppress it almost every time).
  useEffect(() => {
    if (paletteOpen) {
      void capturePaletteOpened(webStorage, getAnalytics());
    }
  }, [paletteOpen]);

  const { value: dndSnoozeUntil } = useStorage("dndSnoozeUntil");

  // Assembled fresh from live state whenever the palette is open — satisfies spec §5.3
  // ("state-dependent commands/labels computed at open time"): by the time `paletteOpen`
  // flips true, this render's deps already reflect current publicMode/aiCapable/snooze/clips.
  // Skipped (stable empty array) while closed, since notifications/conversations/clips churn
  // on effectively every incoming event and the palette isn't mounted to consume the result.
  const paletteCommands = useMemo(
    () =>
      paletteOpen
        ? buildPaletteCommands({
            notifications: notifications ?? [],
            conversations: conversationsList,
            unreadCount: unreadCount ?? 0,
            publicMode,
            onTogglePublicMode: togglePublicMode,
            aiCapable,
            onGlance: handleGlance,
            dndSnoozeUntil,
            dockClips,
            clipCapabilities: clipCaps,
            onOpenSettings: handleOpenSettings,
          })
        : NO_PALETTE_COMMANDS,
    [
      paletteOpen,
      notifications,
      conversationsList,
      unreadCount,
      publicMode,
      togglePublicMode,
      aiCapable,
      handleGlance,
      dndSnoozeUntil,
      dockClips,
      clipCaps,
      handleOpenSettings,
    ]
  );

  // Single seam: instruments all 8 v1 commands at once instead of touching
  // each `run` in paletteCommands.ts (which stays pure/analytics-free).
  const instrumentedPaletteCommands = useMemo(
    () => withCommandAnalytics(getAnalytics(), paletteCommands),
    [paletteCommands]
  );

  // Render the headless list directly — its `.container` is
  // `display: flex; flex-direction: column; height: 100%` and it
  // owns its own search/actions/list padding via the module CSS.
  // An outer padded Box wrapper would both add unwanted insets and
  // break the `height: 100%` (height-100% measures the padded
  // ancestor's content-box). The "Paired with X" chrome lives in
  // the `header` slot below, so the outer Box has nothing else to
  // contribute.
  return (
    <>
      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        commands={instrumentedPaletteCommands}
        labels={COMMAND_PALETTE_LABELS}
      />
      <div className={styles.shell}>
        <div className={styles.main}>
          <SidePanelNotificationList
            notifications={notifications ?? []}
            conversations={conversationsList}
            appIconCache={appIconCache ?? {}}
            isGrouped={isGrouped}
            onToggleGrouped={handleToggleGrouped}
            unreadCount={unreadCount ?? 0}
            onMarkRead={(ids) => {
              void markNotificationsRead(ids);
            }}
            onMarkAllRead={() => {
              void markAllRead();
            }}
            onDismiss={(msgId) => {
              void dismissNotification(msgId);
            }}
            onDismissConversation={(key) => {
              void dismissConversation(key);
            }}
            onDismissAll={({ msgIds, conversationKeys }) => {
              void dismissAllNotifications(msgIds, conversationKeys);
            }}
            onMarkConversationRead={handleMarkConversationRead}
            onSendReply={sendReply}
            onDispatchAction={dispatchAction}
            publicMode={publicMode}
            scrollTo={jump}
            recap={
              <Glance
                publicMode={publicMode}
                provider={provider}
                aiStatus={aiStatus}
                trigger={recapTrigger}
                onJumpTo={(target) => setJump((j) => ({ target, signal: (j?.signal ?? 0) + 1 }))}
              />
            }
            header={
              // The header uses the headless `HeaderBar` from
              // `@reflecto/client-ui` — the same one the extension uses.
              // The Logo is intentionally dropped: the browser tab title
              // prefix `(N) Reflecto` and the favicon already brand the page,
              // and HeaderBar's compact one-row layout is tighter than a
              // centered logo + name + button stack. `InstallPrompt` stays as
              // a sibling so the install banner still renders below the
              // header, scrolling with the rest of the chrome rather than
              // floating awkwardly above the notification list. (InstallPrompt
              // renders nothing on browsers that don't fire
              // `beforeinstallprompt` — Safari, Firefox, every iOS browser —
              // and nothing once installed, so it's safe to always include.)
              <>
                <HeaderBar
                  deviceLabel={androidDeviceName ?? messages.header.pairedFallbackDevice}
                  // Pass `paired` and `sseConnected` THROUGH as `boolean | undefined`
                  // — coercing either to `false` would break the headless's
                  // loading-window invariant: when EITHER value is undefined the
                  // connection live region stays empty so AT doesn't announce a
                  // stale "Disconnected" before the storage promise resolves.
                  // `useStorage` returns the typed value or `undefined` during
                  // the first IDB read, which lines up exactly with the prop
                  // shape — no narrowing needed.
                  paired={paired}
                  sseConnected={sseConnected}
                  onOpenSettings={handleOpenSettings}
                  trailing={
                    // The public-mode toggle and (only when the device is AI-capable) the on-demand AI
                    // recap button live in the header's trailing slot — wrap them so neither is dropped.
                    <>
                      {aiCapable ? (
                        <IconButton
                          icon={Stars01}
                          label={messages.glance.buttonLabel}
                          onClick={handleGlance}
                        />
                      ) : null}
                      <PublicModeToggle
                        active={publicMode}
                        onToggle={togglePublicMode}
                        label={
                          publicMode
                            ? messages.header.publicModeToggleDisable
                            : messages.header.publicModeToggleEnable
                        }
                      />
                    </>
                  }
                  labels={WEB_HEADER_LABELS}
                />
                <UpdatePrompt />
                {/* See main.tsx for why install affordances are gated. */}
                {!import.meta.env.VITE_DISABLE_INSTALL && <InstallPrompt />}
                <PairConfirmCardSlot />
              </>
            }
            enableKeyboard
            focusSignal={focusSignal}
            labels={LIST_LABELS}
            notificationListItemLabels={NOTIFICATION_LABELS}
            conversationNotificationItemLabels={CONVERSATION_LABELS}
          />
        </div>
        <ClipDock
          clips={dockClips}
          // MVP: phone is the only partner; multi-device will read peer count.
          deviceCount={1}
          capabilities={clipCaps}
          labels={CLIP_DOCK_LABELS}
          publicMode={publicMode}
        />
        {/* Keyboard keycap hint as the shell's bottom-most row — below the dock.
          Keyboard nav is always on here, so only !publicMode gates it. SC 1.3.1
          evaluated + accepted: the hint reads after the dock controls (not right
          after the list it describes), but the keycaps are supplementary decoration
          — the E/J/K shortcuts work regardless of reading order — so no
          aria-describedby link to the list is added. */}
        {!publicMode && <FooterHint labels={KEYBOARD_HINT} />}
      </div>
    </>
  );
}
